import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ws } from '@/lib/ws';
import { useAuth } from '@/contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MessageCircle, Send } from 'lucide-react';

function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
}

/**
 * MessagesHeaderButton — TopBar pill for desktop+mobile that exposes:
 *  - an icon trigger
 *  - an unread badge (count of unread DM notifications)
 *  - a popover preview listing recent conversations
 *  - quick links to start a new DM and to open the full Messages page
 *
 * Reuses /api/notifications + /api/conversations (no new endpoints).
 */
export function MessagesHeaderButton() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [convos, setConvos] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(false);

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications', { params: { limit: 200 } });
      const list = Array.isArray(data) ? data : [];
      const count = list.filter((n) => n.kind === 'dm' && !n.is_read).length;
      setUnread(count);
    } catch {
      // soft fail — badge just won't update
    }
  }, [user]);

  // Initial unread fetch + periodic refresh.
  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 60_000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  // Live updates from WS for incoming DMs / notifications.
  useEffect(() => {
    const off = ws.on((evt) => {
      if (!evt) return;
      // Only bump unread when the incoming message is not from the current user.
      if (evt.event === 'dm:message') {
        const senderId = evt.message?.sender_id;
        if (senderId && senderId !== user?.id) setUnread((n) => n + 1);
      } else if (evt.event === 'notification:new' && evt.kind === 'dm') {
        setUnread((n) => n + 1);
      }
    });
    return off;
  }, [user]);

  // Re-sync unread when the route changes (e.g. user opens /messages and we mark-read).
  useEffect(() => {
    const t = setTimeout(refreshUnread, 600);
    return () => clearTimeout(t);
  }, [refreshUnread]);

  // Load conversations only when the popover is opened.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingConvos(true);
      try {
        const { data } = await api.get('/conversations');
        if (!cancelled) setConvos(Array.isArray(data) ? data.slice(0, 6) : []);
      } catch {
        if (!cancelled) setConvos([]);
      } finally {
        if (!cancelled) setLoadingConvos(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const goTo = (path) => {
    setOpen(false);
    nav(path);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Direct messages"
          className="tap-target relative grid h-9 place-items-center rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] px-3 text-muted-foreground transition-colors hover:text-foreground hover:border-[hsl(var(--gold)/0.55)]"
          data-testid="topbar-messages"
        >
          <MessageCircle className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="pointer-events-none absolute -right-1 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full border border-[hsl(var(--app-card))] bg-[hsl(var(--accent))] px-1 font-mono text-[9px] font-semibold leading-none text-white"
              data-testid="topbar-messages-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-80 rounded-2xl border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] p-3 shadow-[var(--shadow-elev-2)]"
        data-testid="topbar-messages-popover"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 feed-accent-text" />
            <span className="font-display text-sm font-semibold">Messages</span>
          </div>
          {unread > 0 && (
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--accent))]">
              {unread} unread
            </span>
          )}
        </div>
        <div className="my-2 h-px w-full bg-[hsl(var(--gold)/0.35)]" />
        <div className="max-h-72 overflow-y-auto" data-testid="topbar-messages-list">
          {loadingConvos && (
            <div className="space-y-2">
              {['a', 'b', 'c', 'd'].map((k) => (
                <div key={`hdr-msg-skel-${k}`} className="flex items-center gap-2 px-1 py-2">
                  <div className="h-8 w-8 animate-pulse rounded-full bg-[hsl(var(--app-bg-2))]" />
                  <div className="flex-1 space-y-1">
                    <div className="h-3 w-1/2 animate-pulse rounded bg-[hsl(var(--app-bg-2))]" />
                    <div className="h-2 w-1/3 animate-pulse rounded bg-[hsl(var(--app-bg-2))]" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loadingConvos && convos.length === 0 && (
            <div className="px-1 py-3 font-mono text-[11px] text-[hsl(var(--muted-foreground))]">
              No conversations yet. Start a new one!
            </div>
          )}
          {!loadingConvos && convos.map((c) => {
            const other = (c.participants || [])[0];
            const initials = (other?.username || '?').slice(0, 2).toUpperCase();
            return (
              <button
                key={c.id}
                onClick={() => goTo(`/messages/${c.id}`)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-[hsl(var(--app-bg-2))]"
                data-testid="topbar-messages-thread"
              >
                <div className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full border border-[hsl(var(--gold)/0.45)] bg-[hsl(var(--app-bg-2))] font-mono text-[10px]">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{other?.display_name || other?.username || 'Unknown'}</div>
                  <div className="truncate font-mono text-[10px] text-[hsl(var(--muted-foreground))]">@{other?.username}</div>
                </div>
                <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">{relativeTime(c.last_message_at)}</div>
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Link
            to="/messages"
            onClick={() => setOpen(false)}
            className="flex-1 rounded-xl border border-[hsl(var(--gold)/0.45)] bg-[hsl(var(--app-card))] px-3 py-2 text-center font-display text-xs font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--app-bg-2))]"
            data-testid="topbar-messages-view-all"
          >
            View all messages
          </Link>
          <Link
            to="/messages"
            onClick={() => setOpen(false)}
            aria-label="Open messages"
            className="grid h-9 w-9 place-items-center rounded-xl border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] text-[hsl(var(--foreground))] hover:border-[hsl(var(--gold)/0.55)]"
            data-testid="topbar-messages-open"
          >
            <Send className="h-4 w-4" />
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default MessagesHeaderButton;
